import { DateTime } from "luxon";
import { Organization } from "../models/interfaces/organization";
import { User } from "../models/interfaces/user";
import { MskScore } from "../models/interfaces/msk-score";

interface hubspotUserDataRow {
    firstName: string,
    lastName: string,
    emailAddress: string,
    lastLoggedIn: string,
    symmioRole: string,
    orgName: string,
    orgIndustry: string,
    orgUserCount: number,
    orgLicenseCount: number,
    plan: string,
}

export interface userMskDataRow {
    fullName: string;
    age: number | null;
    biologicalSex: string;
    riskCode: string;
    mskScorePercentage: number;
    lifestyleScorePercentage: number;
    movementScorePercentage: number;
    behavioralScorePercentage: number;
    bodyCompositionScorePercentage: number;
    breathingScorePercentage: number;
    injuryHistoryScorePercentage: number;
    nutritionScorePercentage: number;
    physicalActivityScorePercentage: number;
    sleepScorePercentage: number;
    toeTouchScorePercentage: number;
    toeTouchPainAreasAndScales: string;
    shoulderMobilityScorePercentage: number;
    shoulderMobilityPainAreasAndScales: string;
    rotationScorePercentage: number;
    rotationPainAreasAndScales: string;
    squatScorePercentage: number;
    squatPainAreasAndScales: string;
    balanceScorePercentage: number;
    balancePainAreasAndScales: string;
    hasShoulderClearingPain: boolean;
    shoulderClearingPainAreasAndScales: string;
    hasSpineClearingPain: boolean;
    spineClearingPainAreasAndScales: string;


}

export default class DataExportUtil {
    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    private static getUserRole = (roles: string[] | null | undefined): string => {
        if (roles && roles.length > 0) {
            if (roles[0] !== null && roles[0] !== '') {
                return roles[0];
            }
        }
        return 'none';
    };

    private static capitalizeName = (name: string): string => {
        if (name === '') {
            return '';
        }

        let capped = name.charAt(0).toUpperCase();
        capped += name.substring(1);
        return capped;
    }

    public static getHubspotUserDataRows(users: User[], orgs: Organization[], onlyRoles: boolean): hubspotUserDataRow[] {
        const rows: hubspotUserDataRow[] = [];

        users.forEach((u) => {
            if (u.organizationId && u.email) {
                const org = orgs.find((o) => o.id === u.organizationId);

                if (org?.testingOrg) {
                    return;
                }

                if (org) {
                    const role = this.getUserRole(u.roles);
                    const hubspotUser: hubspotUserDataRow = {
                        firstName: this.capitalizeName(u.firstName ?? ''),
                        lastName: this.capitalizeName(u.lastName ?? ''),
                        emailAddress: u.email,
                        lastLoggedIn: u.lastLoggedIn
                            ? (() => {
                                const daysDifference = Math.floor(DateTime.now().diff(DateTime.fromISO(u.lastLoggedIn), ["days"]).days);
                                return daysDifference === 1
                                    ? `${daysDifference} day ago`
                                    : `${daysDifference} days ago`;
                            })()
                            : "",
                        symmioRole: this.getUserRole(u.roles),
                        orgName: org.name ?? '',
                        orgIndustry: (org.industry && org.industry.label) ? org.industry.label : '',
                        orgUserCount: org.userCount ?? 0,
                        orgLicenseCount: org.mySymmioLicenseCount ?? 0,
                        plan: org.plan?.label ?? "",
                    }
                    if (onlyRoles) {
                        if (role !== 'none') {
                            rows.push(hubspotUser);
                        }
                    } else {
                        if (role === 'none') {
                            rows.push(hubspotUser);
                        }
                    }
                }
            }
        });

        return rows;
    };

    public static generateHubspotUserCsv(rows: hubspotUserDataRow[]): string {
        let csv = '';

        const HEADERS = 'firstName,lastName,emailAddress,lastLoggedIn,symmioRole,orgName,orgIndustry,orgUserCount,orgLicenseCount,plan\n';

        csv += HEADERS;

        rows.forEach((r) => {
            let row = '';
            row += this.escapeCsvValue(r.firstName) + ',';
            row += this.escapeCsvValue(r.lastName) + ',';
            row += this.escapeCsvValue(r.emailAddress) + ',';
            row += this.escapeCsvValue(r.lastLoggedIn) + ',';
            row += this.escapeCsvValue(r.symmioRole) + ',';
            row += this.escapeCsvValue(r.orgName) + ',';
            row += this.escapeCsvValue(r.orgIndustry) + ',';
            row += r.orgUserCount + ','; // Numbers don't need escaping
            row += r.orgLicenseCount + ','; // Numbers don't need escaping
            row += this.escapeCsvValue(r.plan) + '\n';
            csv += row;
        });

        return csv;
    }

    public static getUserMskDataRows(
        mskScores: MskScore[],
        users: User[]
    ): userMskDataRow[] {
        const rows: userMskDataRow[] = [];

        const userMap = new Map(users.map(u => [u.id, u]));

        mskScores.forEach((score) => {
            if (!score.percentage) {
                return;
            }
            const user = userMap.get(score.userId);

            if (!user) return;

            const age = user.birthdate
                ? Math.floor(DateTime.now().diff(DateTime.fromISO(user.birthdate), 'years').years)
                : null;

            rows.push({
                fullName: `${user.firstName ? this.capitalizeName(user.firstName) : ''} ${user.lastName ? this.capitalizeName(user.lastName) : ''}`,
                age,
                biologicalSex: user.biologicalSex ?? '',
                riskCode: score.riskFactor?.riskCode ?? '',
                mskScorePercentage: score.percentage,
                lifestyleScorePercentage: score.lifestyleScore?.percentage ?? 0,
                movementScorePercentage: score.movementScore?.percentage ?? 0,
                behavioralScorePercentage: score.lifestyleScore?.behavioralScore?.percentage ?? 0,
                bodyCompositionScorePercentage: score.lifestyleScore?.bodyCompositionScore?.percentage ?? 0,
                breathingScorePercentage: score.lifestyleScore?.breathingScore?.percentage ?? 0,
                injuryHistoryScorePercentage: score.lifestyleScore?.injuryHistoryScore?.percentage ?? 0,
                nutritionScorePercentage: score.lifestyleScore?.nutritionScore?.percentage ?? 0,
                physicalActivityScorePercentage: score.lifestyleScore?.physicalActivityScore?.percentage ?? 0,
                sleepScorePercentage: score.lifestyleScore?.sleepScore?.percentage ?? 0,
                toeTouchScorePercentage: score.movementScore?.toeTouchScore?.percentage ?? 0,
                toeTouchPainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.toeTouchScore?.painAreasAndScales
                ),
                shoulderMobilityScorePercentage: score.movementScore?.shoulderMobilityScore?.percentage ?? 0,
                shoulderMobilityPainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.shoulderMobilityScore?.painAreasAndScales
                ),
                rotationScorePercentage: score.movementScore?.rotationScore?.percentage ?? 0,
                rotationPainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.rotationScore?.painAreasAndScales
                ),
                squatScorePercentage: score.movementScore?.squatScore?.percentage ?? 0,
                squatPainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.squatScore?.painAreasAndScales
                ),
                balanceScorePercentage: score.movementScore?.balanceScore?.percentage ?? 0,
                balancePainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.balanceScore?.painAreasAndScales
                ),
                hasShoulderClearingPain: score.movementScore?.hasShoulderClearingPain ?? false,
                shoulderClearingPainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.shoulderClearingPainAreaAndScale
                ),
                hasSpineClearingPain: score.movementScore?.hasSpineClearingPain ?? false,
                spineClearingPainAreasAndScales: this.formatPainAreaAndScale(
                    score.movementScore?.spineClearingPainAreaAndScale
                ),

            });
        });

        return rows;
    }

    public static generateUserMskDataCsv(rows: userMskDataRow[]): string {
        let csv = '';

        const HEADERS = [
            'fullName',
            'age',
            'biologicalSex',
            'riskCode',
            'mskScorePercentage',
            'lifestyleScorePercentage',
            'movementScorePercentage',
            'behavioralScorePercentage',
            'bodyCompositionScorePercentage',
            'breathingScorePercentage',
            'injuryHistoryScorePercentage',
            'nutritionScorePercentage',
            'physicalActivityScorePercentage',
            'sleepScorePercentage',
            'toeTouchScorePercentage',
            'toeTouchPainAreasAndScales',
            'shoulderMobilityScorePercentage',
            'shoulderMobilityPainAreasAndScales',
            'rotationScorePercentage',
            'rotationPainAreasAndScales',
            'squatScorePercentage',
            'squatPainAreasAndScales',
            'balanceScorePercentage',
            'balancePainAreasAndScales',
            'hasShoulderClearingPain',
            'shoulderClearingPainAreasAndScales',
            'hasSpineClearingPain',
            'spineClearingPainAreasAndScales'
        ].join(',') + '\n';

        csv += HEADERS;

        rows.forEach((r) => {
            let row = '';

            row += this.escapeCsvValue(r.fullName) + ',';
            row += (r.age ?? '') + ',';
            row += this.escapeCsvValue(r.biologicalSex) + ',';
            row += this.escapeCsvValue(r.riskCode) + ',';
            row += r.mskScorePercentage + ',';
            row += r.lifestyleScorePercentage + ',';
            row += r.movementScorePercentage + ',';
            row += r.behavioralScorePercentage + ',';
            row += r.bodyCompositionScorePercentage + ',';
            row += r.breathingScorePercentage + ',';
            row += r.injuryHistoryScorePercentage + ',';
            row += r.nutritionScorePercentage + ',';
            row += r.physicalActivityScorePercentage + ',';
            row += r.sleepScorePercentage + ',';
            row += r.toeTouchScorePercentage + ',';
            row += this.escapeCsvValue(r.toeTouchPainAreasAndScales) + ',';
            row += r.shoulderMobilityScorePercentage + ',';
            row += this.escapeCsvValue(r.shoulderMobilityPainAreasAndScales) + ',';
            row += r.rotationScorePercentage + ',';
            row += this.escapeCsvValue(r.rotationPainAreasAndScales) + ',';
            row += r.squatScorePercentage + ',';
            row += this.escapeCsvValue(r.squatPainAreasAndScales) + ',';
            row += r.balanceScorePercentage + ',';
            row += this.escapeCsvValue(r.balancePainAreasAndScales) + ',';
            row += (r.hasShoulderClearingPain ? 'true' : 'false') + ',';
            row += this.escapeCsvValue(r.shoulderClearingPainAreasAndScales) + ',';
            row += (r.hasSpineClearingPain ? 'true' : 'false') + ',';
            row += this.escapeCsvValue(r.spineClearingPainAreasAndScales);

            csv += row + '\n';
        });

        return csv;
    }

    // Escapes values that might contain commas, quotes, or newlines
    private static escapeCsvValue(value: string): string {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
        }
        return value;
    }

    private static formatPainAreaAndScale(
        data?: { area: string; scale: number; direction: string }[]
    ): string {
        return (data ?? [])
            .map(p => `${p.direction ?? ''} ${p.area ?? ''}-${p.scale ?? ''}`.trim())
            .join(';');
    }

    // #endregion Public Methods
}
